import React, { useState } from 'react';
import './SequenceStyles.css';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString() + ' • ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const DeliverySummary = ({ pref }) => {
  if (!pref) return <div className="sv-muted">No delivery preference</div>;
  const days = pref.allDays ? ['All days'] : (pref.selectedDays || []);
  const time = pref.deliveryTimeType === 'custom'
    ? `${pref.customTimeFrom || '—'} → ${pref.customTimeTo || '—'}`
    : (pref.deliveryTimeType || '—');

  return (
    <div className="sv-delivery-box">
      <div className="sv-delivery-row">
        <i className="bi bi-calendar-event sv-icon" />
        <div className="sv-delivery-chips">
          {days.map((d, i) => <span key={i} className="sv-chip">{d}</span>)}
        </div>
      </div>
      <div className="sv-delivery-row">
        <i className="bi bi-clock sv-icon" />
        <div className="sv-delivery-time">{time}</div>
        <div className="sv-delivery-once">{pref.allowOnce ? 'Once' : 'Repeat'}</div>
      </div>
    </div>
  );
};

const MediaArea = ({ tpl }) => {
  const [idx, setIdx] = useState(0);
  const hasCarousel = tpl.carousel_json && Array.isArray(tpl.carousel_json.carousel_body) && tpl.carousel_json.carousel_body.length > 0;
  const carouselItems = hasCarousel ? tpl.carousel_json.carousel_body : [];
  const mediaUrl = tpl.template_media_url || tpl.carousel_json?.media_url || '';

  if (tpl.template_type && tpl.template_type.toLowerCase() === 'video' && tpl.template_media_url) {
    return <video className="sv-media" controls src={tpl.template_media_url} />;
  }

  if (hasCarousel) {
    const item = carouselItems[idx] || {};
    // Use carousel media_url if present, else fallback to template_media_url
    const src = item.media_url || tpl.carousel_json?.media_url || mediaUrl;
    return (
      <div className="sv-carousel-vertical">
        <img className="sv-media" src={src} alt={tpl.template_name || 'carousel'} />
        <div className="sv-carousel-info">
          <div className="sv-carousel-caption">{item.body || tpl.template_message_body || ''}</div>
          <div className="sv-carousel-controls">
            <button className="btn btn-sm btn-light" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}>◀</button>
            <div className="sv-carousel-index">{idx + 1}/{carouselItems.length}</div>
            <button className="btn btn-sm btn-light" onClick={() => setIdx(i => Math.min(carouselItems.length - 1, i + 1))} disabled={idx === carouselItems.length - 1}>▶</button>
          </div>
        </div>
      </div>
    );
  }

  if (mediaUrl) {
    return <img className="sv-media" src={mediaUrl} alt={tpl.template_name || 'media'} />;
  }

  if (tpl.template_type && tpl.template_type.toLowerCase() === 'text') {
    return <div className="sv-media-text">{tpl.template_message_body || 'Text'}</div>;
  }

  return <div className="sv-media-placeholder">No media</div>;
};

const StepCard = ({ step }) => {
  const tplWrap = step.templateDetails || {};
  const tpl = tplWrap.template || {};
  const bindings = tplWrap.bindings || {};

  return (
    <div className="sv-timeline-item">
      <div className="sv-timeline-marker" />
      <div className="sv-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-step-label">Step {step.stepNumber}</div>
            <div className="sv-step-sub">{step.sendAfter} {step.sendUnit}</div>
          </div>
          <div className="sv-template-title">{tpl.template_name || tpl.template_name || (tpl._id ? tpl._id.slice(0,8) : '—')}</div>
        </div>

        <div className="sv-card-media">
          <MediaArea tpl={tpl} />
        </div>

        <div className="sv-card-body">
          <div className="sv-row">
            <div className="sv-row-title">Message</div>
            <div className="sv-message">{tpl.template_message_body ? tpl.template_message_body.split('\n').slice(0,3).join(' ') : '—'}</div>
          </div>

          <div className="sv-row">
            <div className="sv-row-title">Buttons</div>
            <div className="sv-btns">
              {Array.isArray(tpl.button_json) && tpl.button_json.length > 0 ? tpl.button_json.map((b, i) => {
                const label = b.button || b.content || b.type || `btn${i+1}`;
                const cls = b.type === 'visit_website' ? 'sv-btn-web' : b.type === 'call_phone' ? 'sv-btn-call' : 'sv-btn-qr';
                return <span key={i} className={`sv-btn-badge ${cls}`}>{label}</span>;
              }) : <span className="sv-muted">No buttons</span>}
            </div>
          </div>

          <div className="sv-row">
            <div className="sv-row-title">Bindings</div>
            <div className="sv-bindings-compact">
              {Object.keys(bindings).length === 0 ? <span className="sv-muted">No bindings</span> :
                Object.entries(bindings).map(([k,v]) => <span key={k} className="sv-binding-pill"><strong>{k}</strong>: {v}</span>)
              }
            </div>
          </div>

          {step.showDeliveryPref && step.deliveryPref && (
            <div className="sv-row">
              <div className="sv-row-title">Delivery</div>
              <DeliverySummary pref={step.deliveryPref} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SequenceView = ({ sequence, onClose }) => {
  if (!sequence) return null;
  const ddRaw = sequence.defaultDeliveryPreference;
  const dd = Array.isArray(ddRaw) ? (ddRaw[0] || null) : (ddRaw || null);

  return (
    <div className="sv-drawer-wrap">
      <div className="sv-drawer">
        <div className="sv-drawer-header">
          <div>
            <h3 className="sv-drawer-title">{sequence.name || sequence.title || 'Sequence'}</h3>
            <div className="sv-drawer-sub">Based on: {sequence.contact_name || sequence.contactid || '—'}</div>
          </div>
          <div className="sv-drawer-actions">
            <div className="sv-meta">{formatDateTime(sequence.createdAt)}</div>
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="sv-drawer-body">
          <aside className="sv-overview">
            <div className="sv-overview-card">
              <div className="ov-title">Overview</div>
              <div className="ov-row"><strong>Header</strong><div>{sequence.header || '—'}</div></div>
              <div className="ov-row"><strong>Send From</strong><div>{sequence.sender || sequence.sendFrom || '—'}</div></div>
              <div className="ov-row"><strong>Trigger</strong><div>{sequence.triggerCondition || '—'}</div></div>

              <div className="ov-title mt-3">Default Delivery</div>
              <DeliverySummary pref={dd} />
            </div>
          </aside>

          <main className="sv-main">
            <div className="sv-timeline">
              {(sequence.steps || []).length === 0 && <div className="sv-empty">No steps defined</div>}
              {(sequence.steps || []).map((st, i) => <StepCard key={st.stepNumber || i} step={st} />)}
            </div>
          </main>
        </div>
      </div>

      <div className="sv-backdrop" onClick={onClose} />
    </div>
  );
};

export default SequenceView;